import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

function SubscribeForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email });
      setDone(true);
      setEmail("");
      if (data.already_subscribed) {
        toast.success("You're already on the list. Thank you.");
      } else {
        toast.success("Welcome — encouragement is on its way.");
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Could not subscribe. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="subscribe-form" className="mt-4">
      <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">
        Encouragement in your inbox
      </p>
      <p className="text-sm text-ink-soft mb-4 leading-relaxed max-w-sm">
        Now and then — a poem, a short essay, or news of a new piece. No spam,
        only quiet words.
      </p>
      {done ? (
        <p
          data-testid="subscribe-confirmation"
          className="font-serif-display italic text-terracotta"
        >
          Thank you for letting me in.
        </p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2 max-w-sm">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="subscribe-email-input"
            className="flex-1 bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="subscribe-submit-btn"
            className="px-5 py-2.5 bg-ink text-paper text-xs tracking-[0.18em] uppercase hover:bg-terracotta transition-colors disabled:opacity-60"
          >
            {loading ? "Sending…" : "Subscribe"}
          </button>
        </div>
      )}
    </form>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-rule mt-24 bg-paper-2/40"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="font-serif-display text-2xl tracking-tight text-ink">
            B.&nbsp;Ruff&nbsp;<span className="text-terracotta">Writes</span>
          </p>
          <p className="mt-3 text-sm text-ink-soft max-w-sm leading-relaxed">
            Stories, poems, and essays designed to encourage hearts and nurture
            faith.
          </p>
          <SubscribeForm />
        </div>

        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">
            Explore
          </p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/book" className="hover:text-terracotta link-underline">The Book</Link></li>
            <li><Link to="/writings" className="hover:text-terracotta link-underline">All Writings</Link></li>
            <li><Link to="/writings?category=childrens_books" className="hover:text-terracotta link-underline">Children's Books</Link></li>
            <li><Link to="/writings?category=poems" className="hover:text-terracotta link-underline">Poetry</Link></li>
            <li><Link to="/writings?category=articles" className="hover:text-terracotta link-underline">Articles</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">
            More
          </p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/listen" className="hover:text-terracotta link-underline">Listen</Link></li>
            <li><Link to="/about" className="hover:text-terracotta link-underline">About Barbara</Link></li>
            <li><Link to="/login" className="hover:text-terracotta link-underline">Admin</Link></li>
          </ul>
          <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mt-6 mb-3">
            Follow
          </p>
          <a
            href="https://www.instagram.com/b.ruff.writes/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="inline-flex items-center gap-2 text-sm text-ink hover:text-terracotta transition-colors"
          >
            <Instagram size={18} />
            <span className="link-underline">@b.ruff.writes</span>
          </a>
        </div>
      </div>
      <div className="border-t border-rule">
        <p className="max-w-6xl mx-auto px-6 lg:px-10 py-5 text-xs text-ink-soft tracking-wide">
          © {new Date().getFullYear()} Barbara Ruffing. Words written in faith.
        </p>
      </div>
    </footer>
  );
}
